import { supabase } from '../config/supabase';
import {
  checkDriverScheduleConflict,
  checkVehicleScheduleConflict,
  formatScheduleConflictMessage,
  resolveCandidateWindow,
} from '../services/fleetSchedule';

export interface TripConflict {
  tripId: string;
  pickup_time: string;
  end_time?: string;
  pickup_address: string;
  status: string;
  conflictType: 'vehicle' | 'driver';
}

const BLOCKING_STATUSES = ['scheduled', 'dispatched', 'in_progress'] as const;

/**
 * Detect vehicle/driver schedule conflicts on the same date.
 * Uses time-window overlap when pickup/end times are provided (EXT.FLEET M2).
 */
export async function findTripAssignmentConflicts(params: {
  date: string;
  vehicleId?: string | null;
  driverId?: string | null;
  excludeTripId?: string;
  pickupTime?: string | null;
  endTime?: string | null;
  bufferMinutes?: number;
}): Promise<TripConflict[]> {
  const candidate = resolveCandidateWindow(params.pickupTime, params.endTime);

  if (candidate && params.vehicleId) {
    const rows = await checkVehicleScheduleConflict({
      vehicleId: params.vehicleId,
      date: params.date,
      pickupTime: candidate.pickupTime,
      endTime: candidate.endTime,
      excludeTripId: params.excludeTripId,
      bufferMinutes: params.bufferMinutes,
    });
    if (rows.length > 0) {
      return rows.map((r) => ({
        tripId: r.conflicting_trip_id,
        pickup_time: r.pickup_time,
        end_time: r.end_time,
        pickup_address: r.pickup_address,
        status: r.status,
        conflictType: 'vehicle',
      }));
    }
  }

  if (candidate && params.driverId) {
    const rows = await checkDriverScheduleConflict({
      driverId: params.driverId,
      date: params.date,
      pickupTime: candidate.pickupTime,
      endTime: candidate.endTime,
      excludeTripId: params.excludeTripId,
      bufferMinutes: params.bufferMinutes,
    });
    if (rows.length > 0) {
      return rows.map((r) => ({
        tripId: r.conflicting_trip_id,
        pickup_time: r.pickup_time,
        end_time: r.end_time,
        pickup_address: r.pickup_address,
        status: r.status,
        conflictType: 'driver',
      }));
    }
  }

  if (candidate) {
    return [];
  }

  // Fallback: legacy same-day assignment check (no candidate window)
  const conflicts: TripConflict[] = [];

  const checks: Array<{
    field: 'vehicle_id' | 'driver_id';
    value?: string | null;
    type: TripConflict['conflictType'];
  }> = [
    { field: 'vehicle_id', value: params.vehicleId, type: 'vehicle' },
    { field: 'driver_id', value: params.driverId, type: 'driver' },
  ];

  for (const { field, value, type } of checks) {
    if (!value) continue;

    let query = supabase
      .from('trips')
      .select('id, pickup_time, end_time, pickup_address, status')
      .eq('date', params.date)
      .eq(field, value)
      .in('status', [...BLOCKING_STATUSES]);

    if (params.excludeTripId) {
      query = query.neq('id', params.excludeTripId);
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message);

    for (const row of data ?? []) {
      conflicts.push({
        tripId: row.id,
        pickup_time: row.pickup_time,
        end_time: row.end_time,
        pickup_address: row.pickup_address,
        status: row.status,
        conflictType: type,
      });
    }
  }

  return conflicts;
}

export function formatTripConflictMessage(conflicts: TripConflict[]): string {
  if (conflicts.length === 0) return '';
  const withEnd = conflicts.every((c) => c.end_time);
  if (withEnd) {
    return conflicts
      .map((c) => {
        const label = c.conflictType === 'vehicle' ? 'Vehicle' : 'Driver';
        return `${label} schedule conflict ${c.pickup_time}–${c.end_time} (${c.pickup_address}) — ${c.status}`;
      })
      .join('\n');
  }
  return conflicts
    .map((c) => {
      const label = c.conflictType === 'vehicle' ? 'Vehicle' : 'Driver';
      return `${label} already assigned to trip at ${c.pickup_time} (${c.pickup_address}) — status: ${c.status}`;
    })
    .join('\n');
}

export { formatScheduleConflictMessage };
